/**
 * The texts of a check result (ADR-0085, DEV_SPEC-0085 Appendix A.1, A.2),
 * kept free of the DOM so Vitest can check them in node. No text calls a
 * claim invented or false (FR-55).
 */

const FOUND = '\u2713';
// U+FE0E asks for the text form, so the sign takes the mark's colour: as an
// emoji, ❔ is a light grey that barely shows on white.
const DEVIATION = '\u26A0\uFE0E';
const OPEN = '\u2754\uFE0E';
const SCHLACHTER = 'Schlachter 1951';

function furtherPlaces(count) {
    if (!count) return '';
    return count === 1
        ? ' (und an einer weiteren Stelle)'
        : ` (und an ${count} weiteren Stellen)`;
}

function quoted(evidence) {
    return evidence && evidence.wording ? `„${evidence.wording}“` : null;
}

function verbatimTitle(item) {
    const locator = item.evidence.locator;
    const detail = item.detail || {};
    const where = detail.at_named_reference === false
        ? `Wörtlich in ${SCHLACHTER}, aber bei ${locator}`
        : `Wörtlich in ${SCHLACHTER}: ${locator}`;
    return where + furtherPlaces(detail.other_matches);
}

function quoteMark(item) {
    if (item.state === 'found') {
        return { symbol: FOUND, title: verbatimTitle(item), wording: quoted(item.evidence) };
    }
    if (item.state === 'deviation') {
        const title = `Weicht von ${SCHLACHTER} ab. Dort, ${item.evidence.locator}:`;
        return { symbol: DEVIATION, title, wording: quoted(item.evidence) };
    }
    return { symbol: OPEN, title: `In ${SCHLACHTER} nicht auffindbar`, wording: null };
}

/**
 * The mark of one item: `{symbol, title, wording}`, or null where FR-49 sets
 * none (a reference that resolves, an English one outside the check).
 */
export function markOf(item) {
    if (item.kind === 'bible_reference') {
        if (item.state !== 'not_verifiable') return null;
        return { symbol: OPEN, title: `Stelle in ${SCHLACHTER} nicht nachweisbar`, wording: null };
    }
    if (item.kind === 'foreign_quote') {
        return {
            symbol: OPEN,
            title: 'Wortlaut nicht geprüft: Übersetzung nicht im Bestand',
            wording: null
        };
    }
    return quoteMark(item);
}

function count(items, kind, state = null) {
    return items.filter(item => item.kind === kind && (state === null || item.state === state)).length;
}

function within(parts) {
    const named = parts.filter(([number]) => number > 0).map(([number, word]) => `${number} ${word}`);
    return named.length ? ` (${named.join(', ')})` : '';
}

function referencePart(items) {
    const checked = items.filter(i => i.kind === 'bible_reference' && i.state !== 'not_checked');
    if (!checked.length) return null;
    const noun = checked.length === 1 ? 'Bibelstelle' : 'Bibelstellen';
    const open = count(checked, 'bible_reference', 'not_verifiable');
    return `${checked.length} ${noun}` + within([[open, 'nicht nachweisbar']]);
}

function quotePart(items) {
    const total = count(items, 'bible_quote');
    if (!total) return null;
    const noun = total === 1 ? 'Zitat' : 'Zitate';
    return `${total} ${noun} aus ${SCHLACHTER}` + within([
        [count(items, 'bible_quote', 'deviation'), 'abweichend'],
        [count(items, 'bible_quote', 'not_verifiable'), 'nicht auffindbar']
    ]);
}

function foreignPart(items) {
    const total = count(items, 'foreign_quote');
    if (!total) return null;
    return total === 1
        ? '1 Zitat aus einer anderen Übersetzung nicht geprüft'
        : `${total} Zitate aus anderen Übersetzungen nicht geprüft`;
}

/** The summary line of Appendix A.2, or null when no part is above zero. */
export function summaryOf(items) {
    if (!items || !items.length) return null;
    const parts = [referencePart(items), quotePart(items), foreignPart(items)].filter(Boolean);
    return parts.length ? `Geprüft: ${parts.join(', ')}` : null;
}
